import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  RoutineConfig,
  RoutineWorkflow,
  useCurrentWorkspace,
  useFindByQuery,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import {
  Colors,
  RoutineExecutionProps,
  RoutineModal,
  RoutineModules,
} from '@gripp/shared-ui';
import { useCallback, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

type AssignedProductListProps = {
  productId: string;
  routines: WorkspaceRoutine[];
};

export const AssignedProductList = ({
  productId,
  routines,
}: AssignedProductListProps) => {
  const currentWorkspace = useCurrentWorkspace();

  // Fetch workflow data to detect alerts
  const { data: workflowData } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: {
      filter: {
        product: { eq: productId },
        and: {
          disabled: {
            ne: true,
          },
        },
      },
    },
    options: { enabled: !!currentWorkspace },
  });

  const [openRoutinePreviewModal, setOpenRoutinePreviewModal] = useState(false);
  const [executeModalProps, setExecuteModalProps] = useState<
    RoutineExecutionProps | undefined
  >();

  // Check if routine has workflows (alerts)
  const getRoutineWorkflows = useCallback(
    (id: string, config: RoutineConfig) => {
      if (!id || !workflowData?.items?.length) return [];
      return workflowData.items.filter(
        (item: RoutineWorkflow) =>
          item.trigger?.config?.workspaceRoutineId === id
      );
    },
    [workflowData]
  );

  const hasWorkflows = (routine: WorkspaceRoutine) => {
    const workflows = getRoutineWorkflows(routine.id ?? '', routine.config);
    return workflows.length > 0;
  };

  const onRoutineClick = (routine: WorkspaceRoutine) => {
    if (!routine) return;
    setOpenRoutinePreviewModal(true);
    setExecuteModalProps({
      workspaceAsset: {
        workspace: currentWorkspace,
      },
      module: RoutineModules?.[routine?.type],
      routine,
      preview: true,
    });
  };

  return (
    <View style={styles.container}>
      {routines.map((routine) => {
        return (
          <TouchableOpacity
            key={routine.id}
            style={styles.routine}
            onPress={() => onRoutineClick(routine)}
          >
            <Text style={styles.routineText}>{routine.name}</Text>
            <View style={styles.iconsContainer}>
              {hasWorkflows(routine) && (
                <MaterialCommunityIcons
                  name="alarm-check"
                  size={16}
                  color={Colors.primaryDark}
                  style={styles.alertIcon}
                />
              )}
              <MaterialCommunityIcons
                name="chevron-right"
                size={24}
                color={Colors.blue}
              />
            </View>
          </TouchableOpacity>
        );
      })}
      {openRoutinePreviewModal && executeModalProps && (
        <RoutineModal
          {...executeModalProps}
          visible={openRoutinePreviewModal}
          onCanceled={() => {
            setOpenRoutinePreviewModal(false);
            setExecuteModalProps(undefined);
          }}
          onCompleted={async () => {
            setOpenRoutinePreviewModal(false);
          }}
          showPortal
          showNativeModal
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    gap: 12,
  },
  routine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: Colors.blueLight,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
  },
  routineText: {
    color: Colors.blue,
    fontSize: 16,
    fontWeight: '500',
  },
  iconsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  alertIcon: {
    marginRight: 4,
  },
});

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      items {
        id
        name
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        disabledBetween {
          start
          end
        }
        createdAt
        updatedAt
        workspace {
          id
        }
      }
    }
  }
`;
